import {
  CustomButton,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomModal,
  CustomTextInput,
  errorDialog,
  successDialog,
  Text,
  useText,
} from '@src/components';

import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types'; // Added PropTypes
import { useDispatch } from 'react-redux';
import { Form } from 'antd';
import { Header } from '@src/components/CustomDialog';
import { currentUserUpdate } from '@src/store/slice/userSlice';
import modalClose from '@src/assets/icons/icon-close.svg';
import modalWarning from '@src/assets/icons/icon-modal-warning.svg';

const EditFirstnameOrSurnameFormModal = ({
  modalVisible,
  handleModalVisible,
  user,
  currentUser,
}) => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // 1. Move the hook call to the top level of the component
  const namePlaceholder = useText('name');
  const headerTitle = `${useText('personalInfo')} - ${useText('edit')}`;
  const yourTransactionHasBeenSuccessfullyCompleted = useText('yourTransactionHasBeenSuccessfullyCompleted');

  useEffect(() => {
    if (user?.name) {
      form.setFieldsValue({
        name: user?.name,
      });
    }
  }, [user, form]);

  const getLabelName = () => (user?.inputName === 'name' ? 'name' : 'surname');

  const handleClose = useCallback(() => {
    form.resetFields();
    handleModalVisible(false);
  }, [handleModalVisible, form]);

  const onFinishEdit = useCallback(
    async (values) => {
      const currentName = user?.inputName === 'name' ? values?.name : currentUser?.name;
      const currentSurName = user?.inputName === 'surName' ? values?.name : currentUser?.surName;

      const data = {
        id: user?.id,
        name: currentName?.trim(),
        surName: currentSurName?.trim(),
        userName: currentUser?.userName?.trim(),
        mobilePhones: currentUser?.mobilePhones
          ?.trim()
          ?.replace(/\)/g, '')
          ?.replace(/\(/g, '')
          ?.replace(/-/g, '')
          ?.replace(/ /g, ''),
        email: currentUser?.email?.trim(),
      };

      const action = await dispatch(currentUserUpdate(data));

      if (currentUserUpdate.fulfilled.match(action)) {
        successDialog({
          title: <Text t="success" />,
          message: action?.payload?.message || yourTransactionHasBeenSuccessfullyCompleted,
          onOk: async () => {
            await handleClose();
          },
        });
      } else {
        errorDialog({
          title: <Text t="error" />,
          message: <Text t="transactionNotCompleted" />,
        });
      }
    },
    [handleClose, dispatch, user, currentUser, currentUserUpdate],
  );

  const onCancel = useCallback(() => {
    form.setFieldsValue({
      name: user?.name,
    });
    handleModalVisible(false);
  }, [user, handleModalVisible, form]);

  return (
    <CustomModal
      className="profile-user-modal"
      maskClosable={false}
      footer={false}
      title={
        <Header title={headerTitle} type={'warning'} icon={modalWarning}></Header>
      }
      visible={modalVisible}
      onCancel={handleClose}
      closeIcon={<CustomImage src={modalClose} />}
    >
      <p>
        <Text t="youCanEditAndUpdateYourPersonalInformation" />
      </p>
      <CustomForm
        name="personalInformation"
        onFinish={onFinishEdit}
        form={form}
        initialValues={{
          name: user?.name,
        }}
        awaitLoading={!user?.id}
        autoComplete="off"
        layout={'vertical'}
      >
        <div className="row ">
          <div className="col-md-12">
            <CustomFormItem
              label={<Text t={getLabelName()} />}
              name="name"
              rules={[
                { required: true, message: <Text t="pleaseEnterName" /> },
                { type: 'string', max: 50 },
              ]}
            >
              <CustomTextInput
                id="myProfile-personalInformation-name-textInput"
                data-testid="name"
                // 2. Use the pre-calculated variable here
                placeholder={namePlaceholder}
                maxLength={50}
              />
            </CustomFormItem>
          </div>
        </div>

        <CustomFormItem className="custom-form-footer">
          <CustomButton
            id="myProfile-personalInformation-custom-btn-cancel"
            data-testid="cancel"
            className="custom-btn-cancel"
            onClick={onCancel}
          >
            <span className="submit">
              <Text t="giveUp" />
            </span>
          </CustomButton>
          <CustomButton
            id="myProfile-personalInformation-custom-btn-primary"
            data-testid="submit"
            className="custom-btn-primary"
            type="primary"
            htmlType="submit"
          >
            <span className="submit">
              <Text t="updateBtn" />
            </span>
          </CustomButton>
        </CustomFormItem>
      </CustomForm>
    </CustomModal>
  );
};

// 3. Add PropTypes validation to fix ESLint warnings
EditFirstnameOrSurnameFormModal.propTypes = {
  modalVisible: PropTypes.bool,
  handleModalVisible: PropTypes.func,
  user: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    inputName: PropTypes.string,
  }),
  currentUser: PropTypes.shape({
    name: PropTypes.string,
    surName: PropTypes.string,
    userName: PropTypes.string,
    mobilePhones: PropTypes.string,
    email: PropTypes.string,
  }),
};

export default EditFirstnameOrSurnameFormModal;